import { useRef, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import ModalHeader from '../../../../components/atoms/ModalHeader';
import FormField from '../../../../components/FormField/FormField';
import Button from '../../../../components/atoms/Button';
import FormWrapper from '../../../../components/categories/forms/FormWrapper';
import { z } from 'zod';
import { SubCategory } from '../../../../types/lookups';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  useUpdateOperatorSpecialityMutation,
  useUploadOperatorSpecialityDocumentMutation,
} from '../../../../store/services/lookups';
import { field } from '../../../../components/FormField/interfaces';

const englishRegex = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
const arabicRegex = /^[\u0600-\u06FF0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;

const getEditSubCategorySchema = (t: any) => z.object({
  subCategoryNameEn: z
    .string()
    .min(1, t('categories.validation.subCategoryNameRequired'))
    .regex(englishRegex, t('categories.validation.englishRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  subCategoryNameAr: z
    .string()
    .min(1, t('categories.validation.subCategoryNameRequired'))
    .regex(arabicRegex, t('categories.validation.arabicRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  subCategoryIcon: z
    .any()
    .refine((files) => files && files.length > 0, t('categories.validation.subCategoryImageRequired'))
    .refine(
      (files) => {
        if (!files || files.length === 0) return true;
        const size = files[0]?.originFileObj?.size ?? files[0]?.size;
        return !size || size <= 5 * 1024 * 1024;
      },
      t('categories.validation.maxFileSize5MB')
    ),
});

/**
 * EagerFileField — uploads the file immediately on selection.
 * Skips re-upload when the file already has a URL (pre-filled from existing sub-category).
 */
function EagerFileField({
  name,
  uploadFn,
  onDocumentId,
  onUploadStateChange,
  ...fieldProps
}: Omit<field, 'onChangeFile'> & {
  uploadFn: (formData: FormData) => Promise<any>;
  onDocumentId: (id: string) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const { setValue } = useFormContext();

  const handleChange = async (fileList: any[]) => {
    setValue(name, fileList, { shouldValidate: true });

    const file = fileList?.[fileList.length - 1]?.originFileObj;
    // originFileObj is absent for pre-filled (existing) files — skip upload
    if (!file) return;

    setIsUploading(true);
    onUploadStateChange?.(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadFn(formData);
      const docId =
        res?.payload?.[0]?.id ||
        res?.payload?.id ||
        res?.id ||
        '';
      onDocumentId(docId);
    } catch {
      onDocumentId('');
    } finally {
      setIsUploading(false);
      onUploadStateChange?.(false);
    }
  };

  return (
    <Spin spinning={isUploading} size="small">
      <FormField
        {...fieldProps}
        name={name}
        onChangeFile={handleChange}
      />
    </Spin>
  );
}

interface EditSubCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  subCategory: SubCategory;
}

export default function EditSubCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  subCategory,
}: EditSubCategoryModalProps) {
  const { t } = useTranslation();

  const [updateOperatorSpeciality, { isLoading }] = useUpdateOperatorSpecialityMutation();
  const [uploadSpecialityDocument] = useUploadOperatorSpecialityDocumentMutation();

  const [isUploadingDocs, setIsUploadingDocs] = useState(false);

  // Holds the newly-uploaded document ID; empty means no new file was selected
  const documentIdRef = useRef<string>('');

  const defaultValues = {
    subCategoryNameEn: subCategory.nameEn,
    subCategoryNameAr: subCategory.nameAr,
    subCategoryIcon: subCategory.blobUrl
      ? [
          {
            uid: '-1',
            name: subCategory.fileName || 'current_image.png',
            status: 'done',
            url: subCategory.blobUrl,
          },
        ]
      : [],
  };

  const handleSubmit = async (data: any) => {
    try {
      const payload: Record<string, any> = {
        nameEn: data.subCategoryNameEn,
        nameAr: data.subCategoryNameAr,
      };

      payload.documentId = documentIdRef.current || (subCategory as any).documentId;

      await updateOperatorSpeciality({ id: subCategory.id, body: payload }).unwrap();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to update sub-category:', error);
      const errorMessage =
        error?.data?.message ||
        error?.data?.errors?.default ||
        error?.data?.errors?.[0] ||
        t('categories.subCategoryUpdateFailed');
      message.error(errorMessage);
    }
  };

  const editSubCategorySchema = getEditSubCategorySchema(t);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={false}
      title={
        <ModalHeader
          title={t('categories.editSubCategory')}
          description={t('categories.updateSubCategoryInfo')}
        />
      }
      centered
      width={600}
    >
      <FormWrapper
        schema={editSubCategorySchema}
        onSubmit={handleSubmit}
        values={defaultValues}
      >
        <div className="flex flex-col gap-4 mt-4">
          <FormField
            type="text"
            name="subCategoryNameEn"
            id="subCategoryNameEn"
            label={t('categories.subCategoryEN')}
            placeholder={t('categories.setName')}
            className="!rounded-lg"
          />
          <FormField
            type="text"
            name="subCategoryNameAr"
            id="subCategoryNameAr"
            label={t('categories.subCategoryAR')}
            placeholder={t('categories.setName')}
            className="!rounded-lg"
          />
          <EagerFileField
            type="file"
            name="subCategoryIcon"
            id="subCategoryIcon"
            label={t('categories.subCategoryImage')}
            maxCount={1}
            multiple={false}
            accept="image/png, image/jpeg"
            uploadText={t('categories.dragDropUpload')}
            uploadHints={[
              t('categories.acceptedFileTypes'),
              t('categories.uploadOneFile'),
              t('categories.maxFileSize5MB'),
            ]}
            uploadFn={(fd: FormData) => uploadSpecialityDocument(fd).unwrap()}
            onDocumentId={(id) => { documentIdRef.current = id; }}
            onUploadStateChange={setIsUploadingDocs}
          />
        </div>

        <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={isLoading} disabled={isUploadingDocs}>
            {t('categories.saveSubCategory')}
          </Button>
        </div>
      </FormWrapper>
    </Modal>
  );
}
